import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BOOKING_CUSTOM_FIELD_TYPES } from '../../../constants/widget-constants';
import { CustomFieldsDao } from '../../../db-models/appointment-custom-fields-dao';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { GlobalObjects, Partner } from '../../../models/global';
import { AppointmentState, UserState } from '../../../models/state.model';
import { CustomEventService } from '../../../services/custom-event.service';
import { FormsService } from '../../../services/forms.service';
import { LoggerService } from '../../../services/logger.service';
import { CustomFieldViewComponent } from '../custom-field-view/custom-field-view.component';

@Component({
  selector: 'app-customfields',
  templateUrl: './customfields.component.html',
  styleUrls: ['./customfields.component.scss'],
  viewProviders: [{
    provide: ControlContainer, useExisting: NgForm
  }],
  imports: [NgClass, CustomFieldViewComponent]
})
export class CustomfieldsComponent implements OnInit {

  @Input() appointmentState: AppointmentState;
  @Input() userState: UserState;
  @Input() globals: GlobalObjects;
  @Input() customFields: CustomFieldsDao[];
  @Input() partner: Partner;
  @Input() selectedJsonCustomFields: {
    [key: number]: { type: string; value: any } | any;
  };
  @Input() customFieldValues: {
    [key: number]: {
      type: string, value: any, option_values: {
        custom_field_option_id: number, custom_field_option_value: string,
      }[]
    } | any
  };
  @Input() isMobile: boolean;
  @Input() lang: string;
  @Input() customFieldValuesLSNameKey: string;
  @Input() widgetType: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];

  protected readonly bookingCustomFieldTypes = BOOKING_CUSTOM_FIELD_TYPES;

  private translate = inject(TranslateService);
  private formsService = inject(FormsService);
  private customEventService = inject(CustomEventService);

  ngOnInit() {
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);
    this.customEventService.customerChangedInInternalWidgetEvent.subscribe((data: { customerUuid: number }) => {
      this.getCustomFieldValues(data.customerUuid);
    });

    this.customEventService.removeDependentCustomFieldsEvent.subscribe(() => {
      this.removeDependentCustomFieldsEvent();
    });
  }

  getCustomFieldValues(customerUuid: number) {
    const tempCustomFields = Object.assign([], this.customFields) as CustomFieldsDao[];
    this.customFields = [];
    this.formsService.getCustomFieldValues(customerUuid).subscribe({
      next: (data) => {
        if (tempCustomFields?.length && data?.length) {
          for (const customField of tempCustomFields) {
            const filteredCustomFieldValues = data.filter(filterCustomField => {
              return filterCustomField.custom_field_id === customField.id;
            });
            if (filteredCustomFieldValues.length) {
              switch (customField.type) {
                case this.bookingCustomFieldTypes.TEXT:
                case this.bookingCustomFieldTypes.TEXTAREA:
                case this.bookingCustomFieldTypes.DATE:
                case this.bookingCustomFieldTypes.RADIO:
                  this.customFieldValues[customField.id] = filteredCustomFieldValues[0].value;
                  break;
                case this.bookingCustomFieldTypes.SELECT:
                case this.bookingCustomFieldTypes.CARD_SELECT:
                case this.bookingCustomFieldTypes.IMAGE_SELECT:
                  if (customField.is_multiple_select === 1) {
                    this.customFieldValues[customField.id] = {
                      type: customField.type,
                      value: filteredCustomFieldValues.map(item => Number(item.value))
                    };
                  } else {
                    this.customFieldValues[customField.id] = {
                      type: customField.type,
                      value: Number(filteredCustomFieldValues[0].value)
                    };
                  }
                  break;
                case this.bookingCustomFieldTypes.NUMBER_SELECT:
                  // @TODO: need to handle this once backend is ready
                  break;
                case this.bookingCustomFieldTypes.CHECKBOX:
                  this.customFieldValues[customField.id] = filteredCustomFieldValues[0].value !== undefined ? ["true", "1", true, 1].includes(filteredCustomFieldValues[0].value) ? true : false : false;
                  break;
                case this.bookingCustomFieldTypes.FILE:
                  break;
                default:
              }
            }
          }
        }
        this.customFields = tempCustomFields;
      },
      error: (error: HttpErrorResponse) => LoggerService.log(error)
    });
  }

  removeDependentCustomFieldsEvent(): void {
    this.customFields.forEach(customField => {
      if (customField.customFieldDependencies?.length) {
        customField.customFieldDependencies.forEach(dependentCustomField => {
          delete this.customFieldValues[dependentCustomField.id];
        });

        customField.customFieldDependencies = [];
      }
    });
  }
}
